import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PenLine } from 'lucide-react';

const formatIcons = {
  article_summary: '📄',
  article: '📄',
  podcast: '🎧',
  audio: '🎧',
  video: '🎬',
  media_review: '🎬',
  social_share: '💬',
  person_profile: '👤',
  ai_discovery: '✨',
};

const mediaTones = {
  article_summary: '#E5E2D8',
  article:         '#E5E2D8',
  podcast:         '#E2DED3',
  audio:           '#E2DED3',
  video:           '#DFDBCF',
  media_review:    '#DFDBCF',
  social_share:    '#E7E3D9',
  person_profile:  '#E6E1D6',
  ai_discovery:    '#DFDBCF',
};

const cardTones = ['#FBFAF7', '#F7F4EC', '#F3EFE6', '#FAF6EF'];

const pill = {
  background: 'rgba(0,0,0,0.05)',
  border: '1px solid rgba(0,0,0,0.08)',
  color: 'rgba(0,0,0,0.55)',
};

const FeedItem = memo(({ item, onSave, isSaved, isActive = false, layout = 'list' }) => {
  const isCard = layout === 'card';
  const hasDiscussions = (item.socialLinks?.length ?? 0) > 0;
  const hasRelated = (item.relatedNews?.length ?? 0) > 0;
  const [detailsTab, setDetailsTab] = useState(() => (hasDiscussions ? 'discussions' : 'related'));
  const [isContextOpen, setIsContextOpen] = useState(false);

  const primaryInterest = item.interests?.[0];
  const itemNumber = Number(item.id);
  const cardTone = cardTones[Number.isNaN(itemNumber) ? 0 : Math.abs(itemNumber) % cardTones.length];
  const mediaTone = mediaTones[item.type] || '#E7E2D8';

  const [momentModes, setMomentModes] = useState({});
  const toggleMode = useCallback((idx, mode) => {
    setMomentModes(prev => ({ ...prev, [idx]: prev[idx] === mode ? null : mode }));
  }, []);

  const getAbstract = useCallback((text) => {
    const words = text.split(' ');
    if (words.length <= 8) return text;
    return words.slice(0, Math.max(Math.ceil(words.length * 0.4), 5)).join(' ') + '.';
  }, []);

  const getDrillDown = useCallback((text) => {
    const parts = text.split(/,\s+/).map(p => p.trim()).filter(p => p.length > 8);
    if (parts.length >= 2) return parts.slice(0, 3);
    const sentences = text.match(/[^.!?]+[.!?]*/g)?.map(s => s.trim()).filter(s => s.length > 10) || [];
    if (sentences.length >= 2) return sentences.slice(0, 3);
    return [text, 'This has broader implications for the wider context.', 'Further detail is available in the source material.'];
  }, []);

  const keyMoments = useMemo(() => {
    const content = (item.content || '').trim();
    if (!content) return [];
    const sentences = content
      .split(/(?<=[.!?])\s+/)
      .map(p => p.trim())
      .filter(Boolean)
      .map(p => p.replace(/[.!?]+$/, ''));
    if (sentences.length > 1) return sentences.slice(0, 7);
    return content.split(/,\s+/).map(p => p.trim()).filter(Boolean).slice(0, 7);
  }, [item.content]);

  useEffect(() => {
    if (detailsTab === 'discussions' && !hasDiscussions && hasRelated) setDetailsTab('related');
    if (detailsTab === 'related' && !hasRelated && hasDiscussions) setDetailsTab('discussions');
  }, [item.id, detailsTab, hasDiscussions, hasRelated]);

  useEffect(() => { setIsContextOpen(false); }, [item.id]);
  useEffect(() => { if (!isActive) setIsContextOpen(false); }, [isActive]);

  const handleSave = useCallback((e) => { e.stopPropagation(); onSave?.(item.id); }, [onSave, item.id]);
  const stopProp = useCallback((e) => { e.stopPropagation(); }, []);
  const getIcon = useCallback((type) => formatIcons[type] || '📄', []);

  if (isCard) {
    return (
      <article className="flex flex-col gap-2.5 w-full">

        {/* ── Main article card ── */}
        <div
          className="rounded-[12px] overflow-hidden flex flex-col"
          style={{
            backgroundColor: cardTone,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            height: 280,
          }}
        >
          {/* Media area */}
          <div className="relative flex-shrink-0 h-[130px] w-full" style={{ backgroundColor: mediaTone }}>
            {item.imageUrl ? (
              <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[64px] leading-none select-none">
                  {item.avatar || getIcon(item.type)}
                </span>
              </div>
            )}

            {/* Interest pill */}
            {primaryInterest && (
              <div
                className="absolute left-3 top-3 px-2.5 py-1 rounded-full bg-[#F8F5EE] text-[9px] uppercase font-semibold"
                style={{ letterSpacing: '1.5px', border: '1px solid rgba(0,0,0,0.10)', color: 'rgba(0,0,0,0.55)' }}
              >
                {primaryInterest}
              </div>
            )}

            {/* Save button */}
            <button
              type="button"
              onClick={handleSave}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white flex items-center justify-center"
              style={{ border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
              aria-label={isSaved ? 'Unsave' : 'Save'}
            >
              <span className="text-[13px]">🔖</span>
            </button>
          </div>

          {/* Info */}
          <div className="px-4 pt-3.5 pb-4 flex-1 flex flex-col justify-between overflow-hidden">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase font-semibold truncate" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
                {item.source || item.author}
              </span>
              <span className="text-[10px] uppercase font-semibold flex-shrink-0" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
                {item.date ?? item.timestamp}
              </span>
            </div>

            <h2 className="mt-2 font-instrument text-[28px] leading-[1.25] text-[#111] line-clamp-2">
              {item.title}
            </h2>

            <div className="my-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.07)' }} />

            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] uppercase font-semibold truncate" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
                {item.author || item.source}
              </span>
              {item.sourceUrl && (
                <button
                  onClick={(e) => { stopProp(e); window.open(item.sourceUrl, '_blank'); }}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase font-semibold"
                  style={{ letterSpacing: '1px', ...pill }}
                >
                  ↗ Source
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Key Moments card ── */}
        <div
          className="rounded-[12px] px-5 py-4"
          style={{
            backgroundColor: '#F7F4EC',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          }}
        >
          <h3 className="font-instrument text-[22px] leading-[28px] text-[#111] mb-4">
            Key Moments
          </h3>

          {(keyMoments.length > 0 ? keyMoments : [item.content]).map((moment, i) => {
            const mode = momentModes[i];
            return (
              <div key={`${item.id}-m-${i}`}>
                {i > 0 && <div className="my-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.07)' }} />}
                <div className="flex items-start gap-3">
                  <span className="min-w-[28px] text-[11px] font-semibold leading-[24px]" style={{ color: 'rgba(0,0,0,0.30)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <p className="text-[15px] leading-[24px]" style={{ color: 'rgba(0,0,0,0.75)' }}>
                      {moment}
                    </p>
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={(e) => { stopProp(e); toggleMode(i, 'abstract'); }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[9px] uppercase font-semibold transition-all"
                        style={{
                          letterSpacing: '1.5px',
                          ...(mode === 'abstract'
                            ? { background: 'rgba(6,78,59,0.12)', border: '1px solid rgba(6,78,59,0.20)', color: '#064e3b' }
                            : pill),
                        }}
                      >
                        ↑ Abstract
                      </button>
                      <button
                        onClick={(e) => { stopProp(e); toggleMode(i, 'drilldown'); }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[9px] uppercase font-semibold transition-all"
                        style={{
                          letterSpacing: '1.5px',
                          ...(mode === 'drilldown'
                            ? { background: 'rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.70)' }
                            : pill),
                        }}
                      >
                        ↓ Drill Down
                      </button>
                    </div>

                    {mode && (
                      <div
                        className="mt-3 rounded-[8px] p-3"
                        style={{
                          background: mode === 'abstract' ? 'rgba(6,78,59,0.06)' : 'rgba(0,0,0,0.04)',
                          border: `1px solid ${mode === 'abstract' ? 'rgba(6,78,59,0.12)' : 'rgba(0,0,0,0.07)'}`,
                        }}
                      >
                        {mode === 'abstract' ? (
                          <p className="text-[14px] leading-[22px]" style={{ color: '#064e3b' }}>
                            {getAbstract(moment)}
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {getDrillDown(moment).map((point, pi) => (
                              <li key={pi} className="flex items-start gap-2 text-[14px] leading-[22px]" style={{ color: 'rgba(0,0,0,0.65)' }}>
                                <span className="mt-[9px] w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'rgba(0,0,0,0.30)' }} />
                                {point}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Notes card ── */}
        <div
          className="rounded-[12px] px-4 py-3.5"
          style={{
            backgroundColor: '#FEFCF4',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}
        >
          <div className="flex items-center gap-3">
            <PenLine className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(0,0,0,0.35)' }} strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase font-semibold leading-none mb-0.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
                Notes
              </p>
              <p className="text-[14px] leading-[20px]" style={{ color: 'rgba(0,0,0,0.35)' }}>
                Tap to add…
              </p>
            </div>
            <span className="text-lg" style={{ color: 'rgba(0,0,0,0.30)' }}>+</span>
          </div>
        </div>

        {/* ── Social & Related card ── */}
        {(hasDiscussions || hasRelated) && (
          <div
            className="rounded-[12px] overflow-hidden"
            style={{
              backgroundColor: '#F8F5EE',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <button
              type="button"
              onClick={(e) => { stopProp(e); setIsContextOpen(p => !p); }}
              className="w-full px-4 py-3.5 flex items-center justify-between"
            >
              <span className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
                Social & Related
              </span>
              <span className="text-lg" style={{ color: 'rgba(0,0,0,0.30)' }}>
                {isContextOpen ? '−' : '+'}
              </span>
            </button>

            {isContextOpen && (
              <div className="border-t" style={{ borderColor: 'rgba(0,0,0,0.07)' }}>
                {hasDiscussions && hasRelated && (
                  <div className="flex gap-4 px-4 pt-3">
                    {hasDiscussions && (
                      <button
                        onClick={(e) => { stopProp(e); setDetailsTab('discussions'); }}
                        className="pb-2 text-[10px] uppercase font-semibold border-b-2 transition-colors"
                        style={{
                          letterSpacing: '1.5px',
                          borderColor: detailsTab === 'discussions' ? '#064e3b' : 'transparent',
                          color: detailsTab === 'discussions' ? '#064e3b' : 'rgba(0,0,0,0.35)',
                        }}
                      >
                        Social
                      </button>
                    )}
                    {hasRelated && (
                      <button
                        onClick={(e) => { stopProp(e); setDetailsTab('related'); }}
                        className="pb-2 text-[10px] uppercase font-semibold border-b-2 transition-colors"
                        style={{
                          letterSpacing: '1.5px',
                          borderColor: detailsTab === 'related' ? '#064e3b' : 'transparent',
                          color: detailsTab === 'related' ? '#064e3b' : 'rgba(0,0,0,0.35)',
                        }}
                      >
                        Related
                      </button>
                    )}
                  </div>
                )}

                <div className="px-4 pb-4 pt-3 space-y-2">
                  {detailsTab === 'discussions' && item.socialLinks?.slice(0, 3).map((link, i) => (
                    <button
                      key={`${link.url}-${i}`}
                      onClick={(e) => { stopProp(e); window.open(link.url, '_blank'); }}
                      className="block w-full text-left px-3 py-2.5 rounded-[8px] text-[14px]"
                      style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.07)', color: 'rgba(0,0,0,0.70)' }}
                    >
                      <span className="mr-2">
                        {link.platform === 'Twitter' ? '𝕏' : link.platform === 'Reddit' ? '🔴' : link.platform === 'Hacker News' ? '🟠' : '🔗'}
                      </span>
                      <span className="line-clamp-2">{link.snippet}</span>
                    </button>
                  ))}
                  {detailsTab === 'related' && item.relatedNews?.map((news, i) => (
                    <button
                      key={`${news.url}-${i}`}
                      onClick={(e) => { stopProp(e); window.open(news.url, '_blank'); }}
                      className="block w-full text-left px-3 py-2.5 rounded-[8px] text-[14px]"
                      style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.07)', color: 'rgba(0,0,0,0.70)' }}
                    >
                      {news.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </article>
    );
  }

  // ── List layout ──
  return (
    <article
      className="flex items-start gap-3 px-4 py-3.5 border-b"
      style={{ borderColor: 'rgba(0,0,0,0.07)' }}
    >
      <div
        className="w-10 h-10 rounded-[8px] flex items-center justify-center flex-shrink-0 text-xl"
        style={{ backgroundColor: mediaTone }}
      >
        {getIcon(item.type)}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-instrument text-[20px] leading-tight text-[#111]">{item.title}</h3>
        <p className="text-[10px] uppercase font-semibold mt-0.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.45)' }}>
          {item.source}
        </p>
        <p className="text-[14px] leading-[21px] mt-1.5 line-clamp-2" style={{ color: 'rgba(0,0,0,0.60)' }}>
          {item.content}
        </p>
      </div>
      <button
        type="button"
        onClick={handleSave}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
        style={{
          background: isSaved ? '#064e3b' : 'rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
        aria-label={isSaved ? 'Unsave' : 'Save'}
      >
        🔖
      </button>
    </article>
  );
});

FeedItem.displayName = 'FeedItem';

FeedItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    avatar: PropTypes.string,
    imageUrl: PropTypes.string,
    interests: PropTypes.arrayOf(PropTypes.string).isRequired,
    sourceUrl: PropTypes.string,
    date: PropTypes.string,
    timestamp: PropTypes.string,
    author: PropTypes.string,
    socialLinks: PropTypes.arrayOf(PropTypes.shape({
      platform: PropTypes.string,
      url: PropTypes.string,
      snippet: PropTypes.string,
    })),
    relatedNews: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })),
  }).isRequired,
  onSave: PropTypes.func,
  isSaved: PropTypes.bool,
  layout: PropTypes.oneOf(['list', 'card']),
  isActive: PropTypes.bool,
};

export default FeedItem;
